import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Lock, User, Phone, Crown, Check, Gem } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import { toast } from 'sonner';
import { trackLogin, trackSignUp } from '../utils/analytics';

export default function AuthPage() {
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    rememberMe: false
  });
  const { login, register, user } = useAuth();
  const { settings } = useSettings();
  const navigate = useNavigate();

  // Redirect logged-in users to homepage
  if (user) {
    navigate('/', { replace: true });
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (isLogin) {
        await login(formData.email, formData.password, formData.rememberMe);
        toast.success('Logged in successfully!');
        try { trackLogin('email'); } catch (e) { /* analytics non-critical */ }
        navigate('/');
      } else {
        await register(formData.name, formData.email, formData.password, formData.phone);
        toast.success('Account created successfully!');
        try { trackSignUp('email'); } catch (e) { /* analytics non-critical */ }
        // Use full page redirect after signup to ensure clean state
        window.location.href = '/';
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (typeof detail === 'string') {
        toast.error(detail);
      } else if (Array.isArray(detail)) {
        // Pydantic validation errors come as array of {msg, loc, type, ...}
        const messages = detail.map(e => e.msg || String(e)).join('. ');
        toast.error(messages);
      } else if (error.message?.includes('Network') || !error.response) {
        toast.error('Network error. Please check your internet connection and try again.');
      } else {
        toast.error('Something went wrong. Please try again.');
      }
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-6" data-testid="auth-page">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center space-x-3 mb-6">
            {settings.logo_url ? (
              <img 
                src={`${settings.logo_url}${settings.logo_url?.includes('?') ? '&' : '?'}v=${settings.updated_at || '1'}`}
                alt={settings.store_name || 'Marissa Gold And Diamonds'}
                className="h-16 w-16 object-contain"
              />
            ) : (
              <div className="h-16 w-16 bg-gradient-to-br from-[#C9A967] to-[#B8860B] rounded-lg flex items-center justify-center">
                <Gem className="h-10 w-10 text-white" />
              </div>
            )}
            <span className="text-3xl font-serif font-bold text-[#1a3a5c]">{settings.store_name || 'Marissa Gold And Diamonds'}</span>
          </Link>
          <h1 className="font-serif text-3xl font-bold text-[#1a3a5c] mb-2" data-testid="auth-title">
            {isLogin ? 'Welcome Back' : 'Create Account'}
          </h1>
          <p className="text-gray-600">
            {isLogin ? 'Sign in to your account' : 'Start your jewelry journey'}
          </p>
        </div>

        <div className="bg-white p-8 rounded-lg shadow-lg border border-gray-200">
          <form onSubmit={handleSubmit} className="space-y-6">
            {!isLogin && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Full Name</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c] transition-colors"
                    placeholder="Enter your name"
                    required
                    data-testid="name-input"
                  />
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c] transition-colors"
                  placeholder="Enter your email"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c] transition-colors"
                  placeholder="Enter your password"
                  required
                  data-testid="password-input"
                />
              </div>
            </div>

            {!isLogin && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Phone (Optional)</label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c] transition-colors"
                    placeholder="Enter your phone number"
                    data-testid="phone-input"
                  />
                </div>
              </div>
            )}

            {isLogin && (
              <div className="flex items-center">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, rememberMe: !formData.rememberMe })}
                  className={`w-5 h-5 rounded border-2 flex items-center justify-center mr-3 transition-colors ${
                    formData.rememberMe 
                      ? 'bg-[#1a3a5c] border-[#1a3a5c]' 
                      : 'border-gray-300 hover:border-[#1a3a5c]'
                  }`}
                  data-testid="remember-me-checkbox"
                >
                  {formData.rememberMe && <Check className="h-3 w-3 text-white" />}
                </button>
                <label className="text-sm text-gray-600 cursor-pointer" onClick={() => setFormData({ ...formData, rememberMe: !formData.rememberMe })}>
                  Remember me for 30 days
                </label>
              </div>
            )}

            {isLogin && (
              <div className="text-right">
                <Link
                  to="/forgot-password"
                  className="text-sm text-[#1a3a5c] hover:text-[#C9A967] transition-colors"
                  data-testid="forgot-password-link"
                >
                  Forgot Password?
                </Link>
              </div>
            )}

            <button
              type="submit"
              className="w-full bg-[#1a3a5c] text-white py-3 rounded-sm font-serif tracking-wide hover:bg-[#1f4268] transition-all duration-300 btn-primary"
              data-testid="auth-submit-button"
            >
              {isLogin ? 'Sign In' : 'Create Account'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => setIsLogin(!isLogin)}
              className="text-[#1a3a5c] hover:text-[#D4AF37] transition-colors text-sm"
              data-testid="toggle-auth-mode"
            >
              {isLogin ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}